/**
 * Merchant-Asset API-Naht — echte BFF-Integration (Spec 514/332, Contract v2.14).
 *
 * list/get/create/update/delete proxyen über den Admin-BFF
 * (`/v1/admin/merchant-assets`), der `X-Admin-Key` serverseitig setzt. Die
 * Ressource ist camelCase (eigene Serialisierung, kein PascalCase-Transformer).
 * merchantId: Query bei GET/DELETE, Body bei POST/PUT.
 *
 * Die List-Response ist im BFF angereichert: pro Asset ein abgeleitetes `usedBy`
 * (+ optional `usageUnavailable`) — kein Contract-Feld.
 */
import { get, post, put, del } from './client';
import type {
  AssetType,
  CreateMerchantAssetInput,
  MerchantAsset,
  UpdateMerchantAssetInput,
} from '@/types/merchant-asset';

const BASE = '/admin/merchant-assets';

interface MerchantAssetListResponse {
  assets: MerchantAsset[];
  total: number;
}

export async function listMerchantAssets(
  merchantId: string,
  type?: AssetType,
): Promise<MerchantAsset[]> {
  const params: Record<string, string> = { merchantId };
  if (type) params.type = type;
  const res = await get<MerchantAssetListResponse>(BASE, params);
  return res.assets;
}

export async function getMerchantAsset(assetId: string, merchantId: string): Promise<MerchantAsset> {
  return get<MerchantAsset>(`${BASE}/${encodeURIComponent(assetId)}`, { merchantId });
}

export async function createMerchantAsset(input: CreateMerchantAssetInput): Promise<MerchantAsset> {
  return post<MerchantAsset>(BASE, input);
}

export async function updateMerchantAsset(
  assetId: string,
  input: UpdateMerchantAssetInput,
): Promise<MerchantAsset> {
  return put<MerchantAsset>(`${BASE}/${encodeURIComponent(assetId)}`, input);
}

export async function deleteMerchantAsset(assetId: string, merchantId: string): Promise<void> {
  // `del` takes no query params — merchantId travels in the path query string.
  await del<void>(`${BASE}/${encodeURIComponent(assetId)}?merchantId=${encodeURIComponent(merchantId)}`);
}

export interface AssetUploadUrlInput {
  merchantId: string;
  /** Asset slot — sent as `category` on the wire (Facade contract field name). */
  category: AssetType;
  fileName: string;
  contentType: string;
}

/**
 * Request a pre-signed upload target via the BFF proxy (Spec 514, Phase 1 proxy
 * `POST /v1/admin/assets/upload-url`). Request shape confirmed against Dev
 * (2026-06-19): `{ merchantId, category, contentType, fileName? }` — the slot field
 * is **`category`** (∈ stylesheet|header|background|logo), not `type`; sending
 * `type` answers 400 `"category is required"`. The BFF is a thin verbatim proxy.
 */
export async function getAssetUploadUrl(input: AssetUploadUrlInput): Promise<Record<string, unknown>> {
  return post<Record<string, unknown>>('/admin/assets/upload-url', {
    merchantId: input.merchantId,
    category: input.category,
    contentType: input.contentType,
    fileName: input.fileName,
  });
}

/**
 * Full upload flow: ask the BFF proxy for a pre-signed URL, PUT the file bytes to
 * S3 from the browser, and return the resulting CDN URL for `currentFileUrl`.
 * Response shape confirmed against Dev (2026-06-19): `{ uploadUrl, cdnUrl, assetKey,
 * expiresIn }`; the URL fields are still read defensively to tolerate variants.
 */
const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10 MB (Contract v2.22)

export async function uploadAssetFile(input: AssetUploadUrlInput, file: File): Promise<string> {
  if (file.size > MAX_UPLOAD_SIZE) {
    throw new Error(`File too large: ${Math.round(file.size / 1024 / 1024)} MB (max 10 MB)`);
  }
  const res = await getAssetUploadUrl(input);
  const uploadUrl = (res.uploadUrl ?? res.url ?? res.presignedUrl ?? res.putUrl) as string | undefined;
  const fileUrl = (res.fileUrl ?? res.cdnUrl ?? res.publicUrl ?? res.currentFileUrl ?? res.assetUrl) as string | undefined;
  if (!uploadUrl || !fileUrl) {
    throw new Error('Unexpected upload-url response shape (no upload/file URL)');
  }
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': input.contentType },
  });
  if (!put.ok) {
    throw new Error(`Upload failed: HTTP ${put.status}`);
  }
  return fileUrl;
}
